// src/components/forms/register/BasicInfoSection.jsx
import React, { useState } from 'react';
import { User, Mail, Lock, Eye, EyeOff, Hash } from 'lucide-react';
import { InputField } from '../shared/InputField';

export const BasicInfoSection = ({ formData, setFormData, showPassword, setShowPassword }) => {
  const [errors, setErrors] = useState({
    username: '',
    idNumber: '',
    email: '',
    password: ''
  });

  // Determine label and key based on role
  let idLabel = "ID Number";
  let idValue = formData.idNumber; // Default for other roles (if any)
  let updateIdField = (value) => setFormData({ ...formData, idNumber: value });
  let idField = "idNumber";

  if (formData.role === "student" || formData.role === "alumni") {
    idLabel = "Student/Alumni ID";
    idValue = formData.studentInfo?.studentId || "";
    idField = "studentId";
    updateIdField = (value) =>
      setFormData({
        ...formData,
        studentInfo: {
          ...formData.studentInfo,
          studentId: value,
        },
      });
  } else if (formData.role === "staff" || formData.role === "faculty") {
    idLabel = "Employee/Faculty No.";
    idValue = formData.staffInfo?.staffId || "";
    idField = "staffId";
    updateIdField = (value) =>
      setFormData({
        ...formData,
        staffInfo: {
          ...formData.staffInfo,
          staffId: value,
        },
      });
  }

  // Validation functions
  const validateUsername = (value) => {
    if (!value) {
      setErrors(prev => ({ ...prev, username: 'Username is required' }));
      return false;
    } else if (!/^[a-z]+_[a-z]+$/i.test(value)) {
      setErrors(prev => ({ ...prev, username: 'Username must be in firstname_lastname format' }));
      return false;
    } else {
      setErrors(prev => ({ ...prev, username: '' }));
      return true;
    }
  };

  const validateId = (value) => {
    if (!value) {
      setErrors(prev => ({ ...prev, idNumber: `${idLabel} is required` }));
      return false;
    } else {
      setErrors(prev => ({ ...prev, idNumber: '' }));
      return true;
    }
  };

  const validateEmail = (value) => {
    if (!value) {
      setErrors(prev => ({ ...prev, email: 'Email is required' }));
      return false;
    } else if (!/^[^\s@]+@(usiu\.ac\.ke|gmail\.com)$/.test(value)) {
      setErrors(prev => ({ ...prev, email: 'Please use your official USIU email (@usiu.ac.ke) or a Gmail account' }));
      return false;
    } else {
      setErrors(prev => ({ ...prev, email: '' }));
      return true;
    }
  };

  const validatePassword = (value) => {
    if (!value) {
      setErrors(prev => ({ ...prev, password: 'Password is required' }));
      return false;
    } else if (value.length < 6) {
      setErrors(prev => ({ ...prev, password: 'Password must be at least 6 characters long' }));
      return false;
    } else {
      setErrors(prev => ({ ...prev, password: '' }));
      return true;
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm space-y-4">
      <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Basic Information</h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <InputField
          id="username"
          label="Username (firstname_lastname)"
          required
          icon={<User className="h-5 w-5 text-gray-400" />}
          value={formData.username}
          onChange={(e) => {
            setFormData({ ...formData, username: e.target.value });
            if (errors.username) validateUsername(e.target.value);
          }}
          validate={validateUsername}
          error={errors.username}
        />

        <InputField
          id="idField"
          label={idLabel}
          required
          icon={<Hash className="h-5 w-5 text-gray-400" />}
          value={idValue}
          onChange={(e) => {
            updateIdField(e.target.value);
            if (errors.idNumber) validateId(e.target.value);
          }}
          validate={validateId}
          error={errors.idNumber}
        />
      </div>

      <InputField
        id="email"
        label="Email address"
        type="email"
        required
        icon={<Mail className="h-5 w-5 text-gray-400" />}
        value={formData.email}
        onChange={(e) => {
          setFormData({ ...formData, email: e.target.value });
          if (errors.email) validateEmail(e.target.value);
        }}
        validate={validateEmail}
        error={errors.email}
      />

      <InputField
        id="password"
        label="Password (6 characters with at least one number)"
        type={showPassword ? "text" : "password"}
        required
        icon={<Lock className="h-5 w-5 text-gray-400" />}
        value={formData.password}
        onChange={(e) => {
          setFormData({ ...formData, password: e.target.value });
          if (errors.password) validatePassword(e.target.value);
        }}
        validate={validatePassword}
        error={errors.password}
        minLength={6}
        endAdornment={
          <button
            type="button"
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
            onClick={() => setShowPassword(!showPassword)}
          >
            {showPassword ? <EyeOff className="h-5 w-5 text-gray-400" /> : <Eye className="h-5 w-5 text-gray-400" />}
          </button>
        }
      />

      {/* Intern Management Section - only for staff */}
      {formData.role === "staff" && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
          <div className="flex items-center mb-3">
            <input
              id="intern-checkbox"
              type="checkbox"
              checked={formData.isIntern || false}
              onChange={(e) => setFormData({
                ...formData,
                isIntern: e.target.checked,
                internStartDate: e.target.checked ? formData.internStartDate : '',
                internEndDate: e.target.checked ? formData.internEndDate : ''
              })}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="intern-checkbox" className="ml-2 block text-sm font-medium text-gray-700">
              I am registering as an intern
            </label>
          </div>
          <p className="text-xs text-gray-600 mb-3">
            Check this if you are an intern counselor (Employee No. is optional for interns)
          </p>

          {formData.isIntern && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <div>
                <label htmlFor="internStartDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Internship Start Date <span className="text-red-500">*</span>
                </label>
                <input
                  id="internStartDate"
                  type="date"
                  value={formData.internStartDate || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    internStartDate: e.target.value
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required={formData.isIntern}
                />
              </div>
              <div>
                <label htmlFor="internEndDate" className="block text-sm font-medium text-gray-700 mb-1">
                  Internship End Date <span className="text-red-500">*</span>
                </label>
                <input
                  id="internEndDate"
                  type="date"
                  value={formData.internEndDate || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    internEndDate: e.target.value
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required={formData.isIntern}
                  min={formData.internStartDate}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BasicInfoSection;

// export const BasicInfoSection = ({ formData, setFormData, showPassword, setShowPassword }) => {
//   // Determine label and key based on role
//   let idLabel = "ID Number";
//   let idValue = formData.idNumber; // Default for other roles (if any)
//   let updateIdField = (value) => setFormData({ ...formData, idNumber: value });

//   if (formData.role === "student") {
//     idLabel = "Student ID";
//     idValue = formData.studentInfo?.studentId || "";
//     updateIdField = (value) =>
//       setFormData({
//         ...formData,
//         studentInfo: {
//           ...formData.studentInfo,
//           studentId: value,
//         },
//       });
//   } else if (formData.role === "staff") {
//     idLabel = "Employee No.";
//     idValue = formData.staffInfo?.staffId || "";
//     updateIdField = (value) =>
//       setFormData({
//         ...formData,
//         staffInfo: {
//           ...formData.staffInfo,
//           staffId: value,
//         },
//       });
//   }

//   return (
//     <div className="bg-white p-6 rounded-lg shadow-sm space-y-4">
//       <h3 className="text-lg font-medium text-gray-900 border-b pb-2">Basic Information</h3>

//       <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//         <InputField
//           id="username"
//           label="Username (firstname_lastname)"
//           required
//           icon={<User className="h-5 w-5 text-gray-400" />}
//           value={formData.username}
//           onChange={(e) => setFormData({ ...formData, username: e.target.value })}
//         />

//         <InputField
//           id="idField"
//           label={idLabel}
//           icon={<Hash className="h-5 w-5 text-gray-400" />}
//           value={idValue}
//           onChange={(e) => updateIdField(e.target.value)}
//         />
//       </div>

//       <InputField
//         id="email"
//         label="Email address"
//         type="email"
//         required
//         icon={<Mail className="h-5 w-5 text-gray-400" />}
//         value={formData.email}
//         onChange={(e) => setFormData({ ...formData, email: e.target.value })}
//       />

//       <InputField
//         id="password"
//         label="Password"
//         type={showPassword ? "text" : "password"}
//         required
//         icon={<Lock className="h-5 w-5 text-gray-400" />}
//         value={formData.password}
//         onChange={(e) => setFormData({ ...formData, password: e.target.value })}
//         minLength={6}
//         endAdornment={
//           <button
//             type="button"
//             className="absolute inset-y-0 right-0 pr-3 flex items-center"
//             onClick={() => setShowPassword(!showPassword)}
//           >
//             {showPassword ? <EyeOff className="h-5 w-5 text-gray-400" /> : <Eye className="h-5 w-5 text-gray-400" />}
//           </button>
//         }
//       />
//     </div>
//   );
// };

// export default BasicInfoSection;
